import { useEffect, useState } from "react";
import { api, currency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Package, AlertTriangle, Receipt, TrendingUp, Leaf } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import DailyPaymentSummary from "@/components/DailyPaymentSummary";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar
} from "recharts";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/reports/dashboard").then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-10 text-muted-foreground">Loading dashboard…</div>;

  const kpis = [
    { label: "Potential Retail Stock Value", value: currency(data.total_stock_value), icon: Package, testid: "kpi-stock-value",
      hint: "At selling prices — not cost" },
    { label: "Today's Sales", value: currency(data.todays_sales), icon: Receipt, testid: "kpi-today-sales" },
    { label: "Weekly Sales", value: currency(data.weekly_sales), icon: TrendingUp, testid: "kpi-weekly-sales" },
    { label: "Low Stock Items", value: data.low_stock_count, icon: AlertTriangle, testid: "kpi-low-stock" },
  ];

  const chartData = (data.daily_revenue || []).map(d => ({
    ...d,
    label: d.date.slice(5), // MM-DD
  }));

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2 flex items-center gap-1.5">
            <Leaf className="w-3 h-3" strokeWidth={1.5} /> Overview
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight" data-testid="dashboard-title">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Retail health at a glance for today —{" "}
            <span className="font-semibold text-foreground" data-testid="dashboard-store-name">
              {data.store_name ? `${data.store_name} store only` : "store"}
            </span>
          </p>
        </div>
        <Badge className="hidden md:inline-flex bg-primary/10 text-primary hover:bg-primary/10">
          {data.product_count} products
        </Badge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-in">
        {kpis.map(k => (
          <Card key={k.label} className="p-5 border-border shadow-sm" data-testid={k.testid}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
                <div className="font-display text-3xl mt-2 tabular-nums">{k.value}</div>
                {k.hint && <div className="text-[11px] text-muted-foreground mt-1" data-testid={`${k.testid}-hint`}>{k.hint}</div>}
              </div>
              <div className="h-9 w-9 rounded-full bg-muted grid place-items-center text-primary">
                <k.icon className="w-4 h-4" strokeWidth={1.5} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {data.missing_cost_count > 0 && (
        <div className="mt-4 rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="missing-cost-notice">
          <span className="font-semibold">Cost value unavailable for {data.missing_cost_count} in-stock products</span>{" "}
          ({currency(data.missing_cost_retail_value)} at selling prices). These have no cost price captured — a missing cost is unknown, not zero.
          {data.costed_product_count > 0 && <> Known cost value of {data.costed_product_count} costed products: {currency(data.known_cost_value)}.</>}
        </div>
      )}

      <DailyPaymentSummary role={user?.role} />

      {/* Revenue trend */}
      <Card className="p-6 mt-6 border-border shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl">Revenue · last 14 days</h3>
          <div className="text-xs text-muted-foreground">ZAR</div>
        </div>
        <div className="h-60" data-testid="revenue-trend-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 600, height: 240 }}>
            <LineChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#eee" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(v) => currency(v)}
                contentStyle={{ borderRadius: 8, borderColor: "#E5E7EB", fontSize: 12, fontFamily: "Manrope" }}
              />
              <Line type="monotone" dataKey="revenue" stroke="#1C3829" strokeWidth={2} dot={{ r: 3, fill: "#1C3829" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Card className="p-6 lg:col-span-2 border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl">Category performance · last 14 days</h3>
          </div>
          {(!data.category_performance || data.category_performance.length === 0) ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              No sales yet — category performance will appear once you conclude your first sale.
            </div>
          ) : (
            <div className="h-56" data-testid="category-perf-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 600, height: 240 }}>
                <BarChart data={data.category_performance} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#eee" vertical={false} />
                  <XAxis dataKey="category" tick={{ fontSize: 10, fill: "#6b7280" }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => currency(v)} contentStyle={{ borderRadius: 8, borderColor: "#E5E7EB", fontSize: 12, fontFamily: "Manrope" }} />
                  <Bar dataKey="revenue" fill="#C05746" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-6 border-border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl">Low stock</h3>
            <Badge className="bg-[#C05746]/10 text-[#C05746] hover:bg-[#C05746]/10">
              {data.low_stock_count}
            </Badge>
          </div>
          {data.low_stock_items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">All stocks are healthy.</div>
          ) : (
            <div className="space-y-2">
              {data.low_stock_items.map(p => (
                <div key={p.id} className="flex items-center justify-between text-sm py-2 border-b last:border-b-0">
                  <div className="truncate max-w-[70%]">
                    <div className="truncate">{p.name}</div>
                    <div className="text-[11px] font-mono text-muted-foreground">{p.sku}</div>
                  </div>
                  <span className="tabular-nums text-[#C05746] font-semibold">{p.quantity_on_hand}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6 mt-6 border-border shadow-sm" data-testid="deals-summary-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">Deals · last 14 days</h3>
          {data.most_used_deal && <Badge className="bg-primary/10 text-primary hover:bg-primary/10">Top: {data.most_used_deal.deal_name}</Badge>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Revenue under deals</div>
            <div className="font-display text-2xl tabular-nums" data-testid="kpi-deals-revenue">{currency(data.deals_revenue || 0)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Discount given</div>
            <div className="font-display text-2xl tabular-nums text-secondary" data-testid="kpi-discount-given">{currency(data.discount_given || 0)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Units sold under deals</div>
            <div className="font-display text-2xl tabular-nums" data-testid="kpi-units-under-deals">{data.units_under_deals || 0}</div>
          </div>
        </div>
        {(!data.deal_performance || data.deal_performance.length === 0) ? (
          <div className="text-sm text-muted-foreground py-2 text-center">No deals applied to sales in the last 14 days.</div>
        ) : (
          <div className="divide-y">
            {data.deal_performance.map((d, i) => (
              <div key={d.deal_id} className="flex items-center justify-between py-2" data-testid={`deal-perf-${i}`}>
                <div className="text-sm">{d.deal_name}</div>
                <div className="text-right text-xs font-mono">
                  <div>{d.uses} sales · {currency(d.revenue)}</div>
                  <div className="text-secondary">− {currency(d.discount)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6 mt-6 border-border shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">Top selling · last 7 days</h3>
        </div>
        {data.top_selling.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">No sales in the last 7 days yet.</div>
        ) : (
          <div className="divide-y">
            {data.top_selling.map((p, i) => (
              <div key={p.sku} className="flex items-center justify-between py-3" data-testid={`top-sell-${i}`}>
                <div>
                  <div className="text-sm font-medium">{p.name}</div>
                  <div className="text-[11px] font-mono text-muted-foreground">{p.sku}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold tabular-nums">{currency(p.revenue)}</div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">{p.qty} sold</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
